import React, { forwardRef, useEffect, useRef, useState } from 'react';
import type { ButtonProps, ButtonSize, ButtonVariant } from './types';

// ─── Variant class maps ──────────────────────────────────────────────────────

export const VARIANT_CLASSES: Record<
  ButtonVariant,
  { base: string; hover: string; active: string; darkBase?: string; darkHover?: string }
> = {
  primary: {
    base: 'bg-brand-500 text-white border-transparent',
    hover: 'hover:bg-brand-600 hover:shadow-brand',
    active: 'active:bg-brand-700',
  },
  secondary: {
    base: 'bg-white text-brand-600 border border-brand-200',
    hover: 'hover:bg-brand-50',
    active: 'active:bg-brand-100',
    darkBase: 'dark:bg-neutral-800 dark:text-brand-400 dark:border-neutral-600',
    darkHover: 'dark:hover:bg-neutral-700',
  },
  danger: {
    base: 'bg-danger-500 text-white border-transparent',
    hover: 'hover:bg-danger-600 hover:shadow-danger',
    active: 'active:bg-danger-700',
  },
  ghost: {
    base: 'bg-transparent text-neutral-700 border-transparent',
    hover: 'hover:bg-neutral-100',
    active: 'active:bg-neutral-200',
    darkBase: 'dark:text-neutral-300',
    darkHover: 'dark:hover:bg-neutral-800',
  },
  link: {
    base: 'bg-transparent text-brand-600 border-transparent',
    hover: 'hover:underline',
    active: 'active:text-brand-700',
    darkBase: 'dark:text-brand-400',
    darkHover: '',
  },
  success: {
    base: 'bg-success-500 text-white border-transparent',
    hover: 'hover:bg-success-600',
    active: 'active:bg-success-700',
  },
  upi: {
    base: 'bg-upi-500 text-white border-transparent',
    hover: 'hover:bg-upi-600',
    active: 'active:bg-upi-600',
  },
};

// ─── Size class maps ─────────────────────────────────────────────────────────

export const SIZE_CLASSES: Record<
  ButtonSize,
  { height: string; padding: string; text: string; radius: string; iconSize: string; spinnerSize: string }
> = {
  sm: {
    height: 'h-8 min-h-[44px] sm:min-h-0',
    padding: 'px-3 py-1',
    text: 'text-sm font-semibold',
    radius: 'rounded',
    iconSize: 'h-4 w-4',
    spinnerSize: 'h-4 w-4',
  },
  md: {
    height: 'h-10 min-h-[44px] sm:min-h-0',
    padding: 'px-4 py-2',
    text: 'text-sm font-semibold',
    radius: 'rounded',
    iconSize: 'h-4 w-4',
    spinnerSize: 'h-4 w-4',
  },
  lg: {
    height: 'h-12',
    padding: 'px-6 py-3',
    text: 'text-base font-semibold',
    radius: 'rounded-md',
    iconSize: 'h-5 w-5',
    spinnerSize: 'h-5 w-5',
  },
  xl: {
    height: 'h-16',
    padding: 'px-8 py-4',
    text: 'text-lg font-bold',
    radius: 'rounded-md',
    iconSize: 'h-5 w-5',
    spinnerSize: 'h-6 w-6',
  },
};

// ─── ButtonSpinner (internal) ─────────────────────────────────────────────────

function ButtonSpinner({ sizeClass }: { sizeClass: string }) {
  return (
    <span className="inline-block animate-spin" role="status" aria-label="Loading">
      <svg
        className={sizeClass}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
    </span>
  );
}

// ─── Button ───────────────────────────────────────────────────────────────────

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    children,
    onClick,
    disabled = false,
    loading = false,
    icon,
    iconPosition = 'left',
    fullWidth = false,
    type = 'button',
    className,
    title,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
  },
  ref,
) {
  const internalRef = useRef<HTMLButtonElement>(null);
  const buttonRef = (ref as React.RefObject<HTMLButtonElement>) ?? internalRef;
  const [lockedWidth, setLockedWidth] = useState<number | undefined>();

  useEffect(() => {
    if (loading && buttonRef.current) {
      setLockedWidth(buttonRef.current.offsetWidth);
    } else {
      setLockedWidth(undefined);
    }
  }, [loading]);

  const v = VARIANT_CLASSES[variant];
  const s = SIZE_CLASSES[size];
  const isInteractive = !disabled && !loading;
  const isIconOnly = !children && !!icon;

  // Focus ring: danger variant gets danger-colored ring, others use global focus-visible from base.css
  const focusRing =
    variant === 'danger'
      ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500 focus-visible:ring-offset-2'
      : '';

  const classes = [
    'inline-flex items-center justify-center gap-2 transition-all duration-150 select-none',
    v.base,
    v.darkBase ?? '',
    isInteractive ? v.hover : '',
    isInteractive ? (v.darkHover ?? '') : '',
    isInteractive ? v.active : '',
    s.height,
    s.padding,
    s.text,
    s.radius,
    focusRing,
    fullWidth ? 'w-full' : '',
    isIconOnly ? `aspect-square !px-0 !py-0 flex items-center justify-center ${s.iconSize}` : '',
    disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
    loading ? 'cursor-not-allowed pointer-events-none' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={buttonRef}
      type={type}
      className={classes}
      onClick={isInteractive ? onClick : undefined}
      disabled={disabled}
      aria-disabled={disabled || loading}
      aria-busy={loading}
      aria-label={ariaLabel}
      title={title}
      data-testid={dataTestId}
      style={lockedWidth !== undefined ? { minWidth: lockedWidth } : undefined}
    >
      {loading ? (
        <ButtonSpinner sizeClass={s.spinnerSize} />
      ) : (
        <>
          {icon && iconPosition === 'left' && (
            <span className={`${s.iconSize} flex items-center`} aria-hidden="true">
              {icon}
            </span>
          )}
          {children}
          {icon && iconPosition === 'right' && (
            <span className={`${s.iconSize} flex items-center`} aria-hidden="true">
              {icon}
            </span>
          )}
        </>
      )}
    </button>
  );
});
